import Avatar from '@components/v2/ui/Avatar/Avatar';
import Icon from '@components/v2/ui/Icon/Icon';
import Tooltip from '@components/v2/ui/Tooltip/Tooltip';
import Typography from '@components/v2/ui/Typography/Typography';
import { Box, Grid } from '@mui/material';
import { Info } from 'lucide-react';
import { AddressDetails } from '../../types/address.types';
import useTeceoStyles from '../../useTeceoStyles';
import { formatCNPJ, formatCPF, formatCompleteAddress } from '../../utils/formatters';
import { Address } from '../CrudTabs/List/AddressListCustomer';
import useWidth from '../Hooks/useWidth';
import SocialMediasLinks, { SocialMediasSectionProps } from './SocialMediasLinks';

const AVATAR_SIZE = 180;

type SignatureProps = {
  name?: string;
  slogan?: string;
  site?: string;
  avatar?: string;
  address?: Address & {
    address: AddressDetails;
  };
  isPJ?: boolean;
  cpfcnpj?: string;
  companyName?: string;
} & SocialMediasSectionProps;

const formatExternalLink = (link: string): string =>
  link.substring(0, 4).toLowerCase() === 'http' ? link : `https://${link}`;

const getSocialSectionMarginTop = (mobileWidth: boolean) => (mobileWidth ? 3 : 0);

const Signature = ({
  medias,
  name,
  slogan,
  site,
  avatar,
  address,
  companyName,
  cpfcnpj,
  isPJ,
}: SignatureProps) => {
  const { classes: teceoClasses } = useTeceoStyles();
  const { mobileWidth } = useWidth();
  const textAlign = mobileWidth ? 'center' : 'left';

  return (
    <Grid
      container
      spacing={2}
      sx={{
        justifyContent: mobileWidth ? 'center' : 'flex-start',
      }}
    >
      <Grid size="auto">
        <Avatar size={AVATAR_SIZE} src={avatar} alt={name} initials={name?.charAt(0)} />
      </Grid>
      <Grid
        size={{
          xs: 12,
          sm: 'grow',
        }}
      >
        <Grid
          container
          style={{ height: '100%' }}
          sx={{
            alignContent: 'space-between',
            justifyContent: 'space-between',
          }}
        >
          <Grid size={12}>
            <Grid
              container
              sx={{
                alignItems: 'center',
                justifyContent: mobileWidth ? 'center' : 'flex-start',
              }}
            >
              <Grid>
                <Typography align={textAlign} variant="h4" color="text.primary">
                  {name}
                  {companyName && isPJ && cpfcnpj && (
                    <Box
                      component="span"
                      sx={{
                        display: 'inline-flex',
                        verticalAlign: 'middle',
                        ml: 1,
                        color: 'text.secondary',
                      }}
                    >
                      <Tooltip
                        TooltipContent={`${companyName} - ${
                          isPJ ? formatCNPJ(cpfcnpj) : formatCPF(cpfcnpj)
                        }`}
                      >
                        <Icon icon={Info} size="lg" />
                      </Tooltip>
                    </Box>
                  )}
                </Typography>
              </Grid>
            </Grid>

            <Typography align={textAlign} variant="body1" color="text.primary">
              {slogan}
            </Typography>
            {address && (
              <Grid
                container
                spacing={1}
                sx={{
                  justifyContent: mobileWidth ? 'center' : 'flex-start',
                  alignItems: 'center',
                }}
              >
                <Grid>
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                    }}
                  >
                    <Typography
                      align={textAlign}
                      variant="h4"
                      color="text.primary"
                      sx={(theme) => ({ fontWeight: theme.typography.fontWeightLight })}
                    >
                      {address.address.city}, {address.address.state}
                    </Typography>
                    <Box sx={{ display: 'flex', ml: 1, color: 'text.secondary' }}>
                      <Tooltip TooltipContent={formatCompleteAddress(address.address)}>
                        <Icon icon={Info} size="lg" />
                      </Tooltip>
                    </Box>
                  </Box>
                </Grid>
              </Grid>
            )}
          </Grid>

          <Grid size={12} sx={{ mt: getSocialSectionMarginTop(mobileWidth) }}>
            <SocialMediasLinks medias={medias} />
            <a
              href={site && formatExternalLink(site)}
              className={teceoClasses.link}
              target="_blank"
              rel="noopener noreferrer external"
            >
              <Typography align={textAlign} variant="h5" color="text.primary">
                {site}
              </Typography>
            </a>
          </Grid>
        </Grid>
      </Grid>
    </Grid>
  );
};

export default Signature;
